/**
 * Terms of Service Page
 *
 * Displays the terms of service content from the database.
 * Content is rendered as Markdown.
 *
 * @module pages/legal/TermsPage
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { legalPagesService } from '../../services/legal-pages.service';
import { Card } from '../../components/ui';
import type { LegalPage } from '@budgira/shared';
import ReactMarkdown from 'react-markdown';

const TermsPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState<LegalPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPage = async () => {
      try {
        const data = await legalPagesService.getLegalPage('terms');
        setPage(data);
      } catch (err) {
        console.error('Failed to load terms page:', err);
        setError(t('errors.generic'));
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [t]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="text-center py-12">
        <p className="text-red-500">{error}</p>
        <Link to="/" className="text-violet-600 hover:underline mt-4 inline-block">
          {t('common.backToHome')}
        </Link>
      </Card>
    );
  }

  return (
    <Card>
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <ReactMarkdown>{page?.content || ''}</ReactMarkdown>
        </article>
        {page?.updatedAt && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-8 pt-4 border-t border-slate-200 dark:border-slate-700">
            {t('legal.lastUpdated')}: {new Date(page.updatedAt).toLocaleDateString()}
          </p>
        )}
      </Card>
  );
};

export default TermsPage;
